import { Grid, Skeleton } from '@mui/material'
import { AppBox, AppButton, AppDialog, AppDialogRefType, AppTypography, Authorize, TextWithToolTip } from 'components'
import { UserMembershipStatus } from 'globals/constants'
import { centsToDollar, getMDYFormattedDate, timestampToDate } from 'globals/functions'
import { useAuthorizeHelper, useMembershipHelper, useRolesHelper, useSubAccountsAndDiscounts } from 'hooks'
import { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store/app.store'
import {
  AuthSliceSelector,
  closeChangeMembershipDialog,
  closeOldPaidMembersSubscriptionDialog,
  openChangeMembershipDialog,
  openOldPaidMembersSubscriptionDialog,
  ProfileModulesliceSelector,
} from 'store/slices'
import { AddSubUserDialog } from '../../add-user-dialog/AddSubUserDialog'
import { ChangePlan } from '../../change-plan/ChangePlan'
import { OldPaidMembersUpgradePlandialog } from '../../old-paid-members-upgrade-plan-dialog/OldPaidMembersUpgradePlanDialog'

export const PaidAccountOwnerBillingCard = () => {
  const { isChangeMembershipDialogOpen, isOldPaidMembersDialogOpen } = useSelector(ProfileModulesliceSelector)
  const { IsTrialUser, IsTrialExpired } = useRolesHelper()
  const {
    CurrentMembership,
    FutureMembership,
    IsOldPaidMember,
    IsSoonToBeCancelledUser,
    IsSoonToBePaused,
    IsCancelledMember,
    IsPausedUser,
  } = useMembershipHelper()
  const { TrialExpiry } = useSelector(AuthSliceSelector)
  const { isAuthorized } = useAuthorizeHelper()
  const { coupon, isGettingAccounts, isGettingCoupons, billableSubAccounts, getSubAccounts } =
    useSubAccountsAndDiscounts({
      applyCoupon: true,
    })
  const dispatch = useAppDispatch()

  const addUserDialogRef = useRef<AppDialogRefType>({})

  const nextPlanStatus = useMemo(() => {
    if (IsSoonToBeCancelledUser()) {
      return 'cancel'
    } else if (IsSoonToBePaused()) {
      return 'pause'
    } else if (CurrentMembership()?.Status === UserMembershipStatus.ACTIVE) {
      return 'renew'
    } else {
      return (
        <>
          change to{' '}
          <b>
            {FutureMembership()?.MembershipTitle} {centsToDollar(FutureMembership()?.MembershipPrice ?? 0)}
          </b>
        </>
      )
    }
    return ''
  }, [CurrentMembership, FutureMembership])
  return (
    <>
      <AppBox border={2} borderColor={'grey.200'} borderRadius={6} padding={2} backgroundColor="white">
        <AppBox display={'flex'} flexDirection={'column'} gap={2}>
          <AppBox paddingBottom={1} borderBottom={1} borderColor={'grey.100'}>
            <TextWithToolTip
              tooltipText={
                <AppTypography>
                  As the <b>Account Owner</b> you are the only person that can make changes to your plan and add users."
                </AppTypography>
              }
            >
              <AppTypography color={'grey.900'} fontSize={16} fontWeight={700}>
                Account Owner
              </AppTypography>
            </TextWithToolTip>
          </AppBox>
          <AppBox>
            <Grid container spacing={2} paddingBottom={2}>
              <Grid item xs={8}>
                <AppTypography fontSize={14} color={'grey.500'}>
                  As the account owner your current plan is:
                </AppTypography>
                <AppTypography>
                  {isGettingAccounts || isGettingCoupons ? (
                    <Skeleton variant="text" width={'30%'} />
                  ) : IsTrialUser() ? (
                    <b>Free Trial Period ending on {timestampToDate(TrialExpiry!)}</b>
                  ) : IsTrialExpired() ? (
                    <b>Trial Ended</b>
                  ) : IsCancelledMember() && !IsSoonToBeCancelledUser() ? (
                    <b>Cancelled</b>
                  ) : IsPausedUser() && !IsSoonToBePaused() ? (
                    <b>Paused</b>
                  ) : (
                    <>
                      <b>
                        {CurrentMembership()?.MembershipTitle} -{' '}
                        {centsToDollar(CurrentMembership()?.MembershipPrice ?? 0)} (
                        {CurrentMembership()?.MembershipBilled}){' '}
                      </b>
                      {!!billableSubAccounts && billableSubAccounts?.length > 0 && (
                        <>
                          X <b>{billableSubAccounts.length + 1}</b> ={' '}
                          {centsToDollar(
                            (CurrentMembership()?.MembershipPrice ?? 0) * (billableSubAccounts?.length + 1)
                          )}
                        </>
                      )}
                    </>
                  )}
                </AppTypography>
              </Grid>
              <Grid item xs={4}>
                <AppBox display={'flex'} gap={2}>
                  <AppDialog
                    open={isChangeMembershipDialogOpen}
                    hideCancelButton={true}
                    hideFooterCloseBtn={true}
                    hideTitleBottomBorder={true}
                    title="Manage Your ParkSitesIO Subscription Plan"
                    maxWidth="md"
                    titleProps={{ fontSize: 20, fontWeight: 400, textAlign: 'center' }}
                    onClose={() => dispatch(closeChangeMembershipDialog())}
                    renderChild={() => (
                      <>
                        {isChangeMembershipDialogOpen && (
                          <ChangePlan
                            closeDialog={() => dispatch(closeChangeMembershipDialog())}
                            openAddUsersDialog={() => {
                              dispatch(closeChangeMembershipDialog())
                              addUserDialogRef.current.openDialog!()
                            }}
                          />
                        )}
                      </>
                    )}
                    renderElement={() => (
                      <AppButton
                        size="large"
                        variant="contained"
                        onClick={() => dispatch(openChangeMembershipDialog())}
                      >
                        Change Plan
                      </AppButton>
                    )}
                  />
                  <AppDialog
                    hideCancelButton={true}
                    hideTitleBottomBorder={true}
                    stickyCloseBtn={true}
                    hideFooterCloseBtn={true}
                    hideTopCloseBtn={true}
                    ref={addUserDialogRef}
                    maxWidth="md"
                    renderChild={(closeDialog) => <AddSubUserDialog closeDialog={closeDialog} />}
                    renderElement={(openDialog) => (
                      <>
                        <AppButton
                          size="large"
                          variant="contained"
                          onClick={() => {
                            if (IsOldPaidMember()) {
                              dispatch(openOldPaidMembersSubscriptionDialog())
                            } else {
                              openDialog()
                            }
                          }}
                          disabled={!isAuthorized({ allowedToPaidUsersAndAdmin: true })}
                        >
                          Add Users
                        </AppButton>
                      </>
                    )}
                  />
                  <AppDialog
                    open={isOldPaidMembersDialogOpen}
                    hideCancelButton={true}
                    stickyCloseBtn={true}
                    hideFooterCloseBtn={true}
                    title="Plan Upgrade Required"
                    maxWidth="md"
                    onClose={() => {
                      dispatch(closeOldPaidMembersSubscriptionDialog())
                    }}
                    renderChild={() => <OldPaidMembersUpgradePlandialog />}
                  />
                </AppBox>
              </Grid>
            </Grid>
            <Authorize allowedToPaidUsers={true} hiddenFromTrialUsers={true} customCondition={!!CurrentMembership()}>
              <AppBox paddingY={2} borderTop={1} borderColor={'grey.200'}>
                <AppBox display={'flex'} gap={2} alignItems={'center'}>
                  <AppBox borderRadius={50} height={'10px'} width={'10px'} backgroundColor={'#2563EB'} />
                  <AppTypography fontSize={14} color={'grey.500'}>
                    Your plan is scheduled to {nextPlanStatus} on{' '}
                    {getMDYFormattedDate(new Date(CurrentMembership()?.CurrentPeriodEnd ?? ''))}
                  </AppTypography>
                </AppBox>
                {billableSubAccounts && billableSubAccounts?.length > 0 && (
                  <AppBox display={'flex'} gap={2} alignItems={'center'}>
                    <>
                      <AppBox borderRadius={50} height={'10px'} width={'10px'} backgroundColor={'#2563EB'} />
                      {isGettingAccounts || isGettingCoupons ? (
                        <Skeleton variant="text" width={'40%'} />
                      ) : (
                        <AppTypography fontSize={14} color={'grey.500'}>
                          You currently qualify for a <b>{coupon?.PercentOff ?? 0}%</b> user volume discount at your
                          next billing
                        </AppTypography>
                      )}
                    </>
                  </AppBox>
                )}
              </AppBox>
            </Authorize>
          </AppBox>
        </AppBox>
      </AppBox>
    </>
  )
}
